import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ViewportService } from 'shared';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';
import { AvatarComponent } from '../../molecules/avatar/avatar.component';
import { YourTeamItem } from './your-teams.interfaces';

const ROWS_PER_EXPAND = 2;

@Component({
  selector: 'ocp-your-teams',
  imports: [TranslatePipe, ThemedIconComponent, AvatarComponent],
  templateUrl: './your-teams.component.html',
  styleUrl: './your-teams.component.scss',
})
export class YourTeamsComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly viewport = inject(ViewportService);

  readonly items = input<YourTeamItem[]>([]);
  readonly showCreate = input(true);

  readonly createTeam = output<void>();
  readonly teamSelect = output<string>();

  private readonly gridRef = viewChild<ElementRef<HTMLElement>>('grid');

  private readonly desktopColumns = signal(3);
  readonly columns = computed(() => (this.viewport.isMobile() ? 2 : this.desktopColumns()));

  private readonly leadingCells = computed(() => (this.showCreate() ? 1 : 0));

  private readonly initialRows = computed(() => (this.viewport.isMobile() ? 2 : 1));

  private readonly revealedRows = linkedSignal<number, number>({
    source: () => this.initialRows(),
    computation: (min, prev) => Math.max(prev?.value ?? min, min),
  });

  private readonly rowsToShowAll = computed(() =>
    Math.ceil((this.items().length + this.leadingCells()) / this.columns())
  );

  readonly showMoreVisible = computed(() => this.revealedRows() < this.rowsToShowAll());

  readonly visibleItems = computed(() => {
    const items = this.items();
    if (!this.showMoreVisible()) {
      return items;
    }

    const visibleCount = this.revealedRows() * this.columns() - this.leadingCells() - 1;
    return items.slice(0, Math.max(0, visibleCount));
  });

  constructor() {
    afterNextRender(() => {
      this.measureColumns();

      const el = this.gridRef()?.nativeElement;
      if (!el || typeof ResizeObserver === 'undefined') {
        return;
      }

      const observer = new ResizeObserver(() => this.measureColumns());
      observer.observe(el);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  protected onShowMore(): void {
    this.revealedRows.update(rows => rows + ROWS_PER_EXPAND);
  }

  private measureColumns(): void {
    if (this.viewport.isMobile()) {
      return;
    }
    const el = this.gridRef()?.nativeElement;
    if (!el) {
      return;
    }
    const template = getComputedStyle(el).gridTemplateColumns;
    if (!template || template === 'none') {
      return;
    }
    const tracks = template.split(' ').filter(Boolean).length;
    if (tracks > 0) {
      this.desktopColumns.set(tracks);
    }
  }
}
